import React, { useEffect, useState } from 'react';
import axios from 'axios';
import Pagination from './Pagination';
import {
  FaPlus, FaTimes, FaSearch, FaEdit, FaCheck,
  FaCheckCircle, FaExclamationCircle, FaUserAlt
} from 'react-icons/fa';

const API_BASE_URL = (process.env.REACT_APP_API_URL || 'https://devserver.spatialgrid.ai/api/v1').replace(/\/$/, '');

const INDUSTRIES = [
  'Manufacturing & Industrial',
  'Healthcare and Pharma',
  'Retail & E-Commerce',
  'Education & Training',
  'Automotive & Transportation',
  'Architecture & Real Estate',
  'Defense & Aerospace',
  'Energy and Mining',
  'Tourism & Hospitality',
];

const inputCls =
  'w-full px-4 py-2.5 rounded-xl bg-[#2d2e32] text-gray-200 border border-[#2e2f33] text-sm outline-none transition-all duration-200 focus:border-[#EE2B2A] focus:ring-2 focus:ring-[#EE2B2A]/10';
const labelCls =
  'block text-[11px] font-semibold uppercase tracking-widest text-gray-500 mb-1.5';

// ── Add User Modal ──────────────────────────────────────────────
function AddUserModal({ show, onClose, onSuccess }) {
  const [formData, setFormData] = useState({
    name: '', email: '', password: '', password_confirmation: '',
    phoneNumber: '', industry: '', avatar: null, coverImage: null,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState('');
  const [status, setStatus] = useState('');

  useEffect(() => {
    if (show) {
      setFormData({ name: '', email: '', password: '', password_confirmation: '', phoneNumber: '', industry: '', avatar: null, coverImage: null });
      setMessage(''); setStatus('');
    }
    document.body.style.overflow = show ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [show]);

  if (!show) return null;

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    if (files) setFormData(p => ({ ...p, [name]: files[0] }));
    else setFormData(p => ({ ...p, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) { setStatus('error'); setMessage('Not authenticated. Please login.'); return; }
    setIsSubmitting(true); setMessage(''); setStatus('');
    try {
      const body = new FormData();
      body.append('name', formData.name);
      body.append('email', formData.email);
      body.append('password', formData.password);
      body.append('password_confirmation', formData.password_confirmation);
      body.append('phoneNumber', formData.phoneNumber);
      body.append('terms_conditions', true);
      if (formData.industry) body.append('industry', formData.industry);
      if (formData.avatar) body.append('avatar', formData.avatar);
      if (formData.coverImage) body.append('coverImage', formData.coverImage);
      const res = await fetch(`${API_BASE_URL}/users/register`, {
        method: 'POST', headers: { Authorization: `Bearer ${accessToken}` }, body,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Failed to add user');
      setStatus('success'); setMessage('User added successfully!');
      onSuccess?.();
      setTimeout(() => onClose(), 1200);
    } catch (err) {
      setStatus('error'); setMessage(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <style>{`
        @keyframes um-fadeIn  { from { opacity:0 } to { opacity:1 } }
        @keyframes um-slideUp { from { opacity:0;transform:translateY(28px) scale(0.97) } to { opacity:1;transform:translateY(0) scale(1) } }
        .um-fadeIn  { animation: um-fadeIn  0.2s ease-out forwards; }
        .um-slideUp { animation: um-slideUp 0.28s cubic-bezier(0.22,1,0.36,1) forwards; }
        .um-scroll::-webkit-scrollbar { display:none; }
        .um-scroll  { scrollbar-width:none; }
      `}</style>
      <div className="um-fadeIn fixed inset-0 z-50 flex items-center justify-center p-5 bg-black/75 backdrop-blur-md">
        <div className="um-slideUp um-scroll relative w-full max-w-2xl max-h-[90vh] overflow-y-auto bg-[#1a1b1f] border border-[#2a2b2f] rounded-2xl shadow-2xl" onClick={e => e.stopPropagation()}>
          {/* Header */}
          <div className="sticky top-0 z-10 flex items-center justify-between px-7 py-5 bg-[#1e1f22] border-b border-[#22232a] rounded-t-2xl">
            <div>
              <h2 className="text-xl font-bold text-gray-100 leading-tight">Add New User</h2>
              <p className="text-xs text-gray-500 mt-0.5">Fill in the details to create a new user account</p>
            </div>
            <button onClick={onClose} className="w-9 h-9 flex items-center justify-center rounded-full bg-[#26272b] border border-[#2e2f33] text-gray-500 text-sm transition-all duration-200 hover:bg-[#EE2B2A] hover:text-white hover:border-[#EE2B2A]">
              <FaTimes />
            </button>
          </div>
          {/* Form */}
          <form onSubmit={handleSubmit} className="px-7 py-6 flex flex-col gap-5">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={labelCls}>Name <span className="text-[#EE2B2A]">*</span></label>
                <input type="text" name="name" value={formData.name} onChange={handleChange} placeholder="Full name" className={inputCls} required />
              </div>
              <div>
                <label className={labelCls}>Email <span className="text-[#EE2B2A]">*</span></label>
                <input type="email" name="email" value={formData.email} onChange={handleChange} placeholder="email@example.com" className={inputCls} required />
              </div>
              <div>
                <label className={labelCls}>Password <span className="text-[#EE2B2A]">*</span></label>
                <input type="password" name="password" value={formData.password} onChange={handleChange} placeholder="Password" className={inputCls} required />
              </div>
              <div>
                <label className={labelCls}>Confirm Password <span className="text-[#EE2B2A]">*</span></label>
                <input type="password" name="password_confirmation" value={formData.password_confirmation} onChange={handleChange} placeholder="Re-enter password" className={inputCls} required />
              </div>
              <div>
                <label className={labelCls}>Phone Number <span className="text-[#EE2B2A]">*</span></label>
                <input type="text" name="phoneNumber" value={formData.phoneNumber} onChange={handleChange} placeholder="+1 234 567 890" className={inputCls} required />
              </div>
              <div>
                <label className={labelCls}>Industry</label>
                <select name="industry" value={formData.industry} onChange={handleChange} className={inputCls}>
                  <option value="">Select industry…</option>
                  {INDUSTRIES.map(i => <option key={i} value={i}>{i}</option>)}
                </select>
              </div>
              <div>
                <label className={labelCls}>Avatar</label>
                <input type="file" name="avatar" onChange={handleChange} accept="image/*" className={inputCls + ' py-1.5'} />
              </div>
              <div>
                <label className={labelCls}>Cover Image</label>
                <input type="file" name="coverImage" onChange={handleChange} accept="image/*" className={inputCls + ' py-1.5'} />
              </div>
            </div>
            {message && (
              <div className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm border ${status === 'success' ? 'bg-green-500/10 border-green-500/25 text-green-400' : 'bg-red-500/10 border-red-500/25 text-red-400'}`}>
                {status === 'success' ? <FaCheckCircle className="shrink-0" /> : <FaExclamationCircle className="shrink-0" />}
                {message}
              </div>
            )}
            <div className="flex items-center justify-end gap-3 pt-4 mt-1 border-t border-[#22232a]">
              <button type="button" onClick={onClose} disabled={isSubmitting} className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-medium bg-[#26272b] hover:bg-[#303136] text-gray-400 hover:text-gray-200 border border-[#2e2f33] transition-all duration-200 disabled:opacity-50">
                <FaTimes className="text-xs" /> Cancel
              </button>
              <button type="submit" disabled={isSubmitting} className="flex items-center gap-2 px-7 py-2.5 rounded-xl text-sm font-semibold text-white bg-[#EE2B2A] disabled:opacity-50">
                {isSubmitting ? (<><span className="w-4 h-4 border-2 border-white/25 border-t-white rounded-full animate-spin" />Adding…</>) : (<><FaPlus />Add User</>)}
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
}

// ── Main Users Component ────────────────────────────────────────
export const Users = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // Inline edit state
  const [editingUserId, setEditingUserId] = useState(null);
  const [editData, setEditData] = useState({ name: '', phoneNumber: '', industry: '', password: '', password_confirmation: '' });
  const [showPasswordEdit, setShowPasswordEdit] = useState(false);
  const [editSaving, setEditSaving] = useState(false);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const limit = 10;

  const fetchUsers = (page = 1) => {
    setLoading(true);
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) { setError('Not authenticated. Please login.'); setLoading(false); return; }
    axios.get(`${API_BASE_URL}/users/users-list?limit=${limit}&page=${page}`, {
      headers: { Authorization: `Bearer ${accessToken}` },
    })
      .then(res => {
        setUsers(res.data.users || []);
        setTotalPages(Math.ceil(res.data.total / limit) || 1);
        setLoading(false);
      })
      .catch(err => {
        if (err.response?.status === 401 || err.response?.status === 403)
          setError('Authentication error. Please login again.');
        else setError(err.message || 'Failed to fetch users');
        setLoading(false);
      });
  };

  useEffect(() => { fetchUsers(currentPage); }, [currentPage]);

  const startEditing = (user) => {
    setEditingUserId(user._id);
    setEditData({ name: user.name || '', phoneNumber: user.phoneNumber || '', industry: user.industry || '', password: '', password_confirmation: '' });
    setShowPasswordEdit(false);
  };
  const cancelEditing = () => { setEditingUserId(null); setShowPasswordEdit(false); };

  const saveEdit = (userId) => {
    if (editData.password && editData.password !== editData.password_confirmation) {
      alert('Passwords do not match.'); return;
    }
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) { setError('Not authenticated.'); return; }
    setEditSaving(true);
    const payload = { name: editData.name, phoneNumber: editData.phoneNumber, industry: editData.industry };
    if (editData.password) { payload.password = editData.password; payload.password_confirmation = editData.password_confirmation; }
    axios.patch(`${API_BASE_URL}/users/admin/edit-user/${userId}`, payload, {
      headers: { Authorization: `Bearer ${accessToken}`, 'Content-Type': 'application/json' },
    })
      .then(() => {
        setUsers(u => u.map(x => x._id === userId ? { ...x, ...payload } : x));
        cancelEditing();
      })
      .catch(err => alert(`Error: ${err.response?.data?.message || err.message}`))
      .finally(() => setEditSaving(false));
  };

  const handleStatusChange = (userId, newStatus) => {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) { setError('Not authenticated.'); return; }
    axios.patch(`${API_BASE_URL}/users/admin/edit-user/${userId}`, { status: newStatus }, {
      headers: { Authorization: `Bearer ${accessToken}`, 'Content-Type': 'application/json' },
    })
      .then(() => setUsers(u => u.map(x => x._id === userId ? { ...x, status: newStatus } : x)))
      .catch(err => alert(`Error: ${err.response?.data?.message || err.message}`));
  };

  const filteredUsers = users.filter(u =>
    (u.name || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
    (u.email || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
    (u.phoneNumber || '').toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="flex-1 p-6 overflow-x-hidden bg-[#121316] min-h-screen">
      {error && (
        <div className="p-4 mb-6 rounded-xl bg-red-500/20 text-red-400 border border-red-500/30">
          <div className="flex items-center gap-3">
            <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
            <span className="font-medium text-sm">{error}</span>
          </div>
        </div>
      )}

      <div className="bg-[#1E1F22] rounded-2xl p-6 shadow-2xl border border-gray-800">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-100 mb-1">Users</h1>
            <p className="text-gray-400 text-sm">Manage your platform users</p>
          </div>
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 w-full md:w-auto">
            {/* Search */}
            {/* <div className="relative flex items-center w-full sm:w-[320px] h-[40px] border border-[#C4C7C7] rounded-[4px] bg-[#2d2e32] focus-within:border-[#EE2B2A]">
              <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm pointer-events-none" />
              <input
                type="text"
                placeholder="Search users..."
                value={searchQuery}
                onChange={e => { setSearchQuery(e.target.value); setCurrentPage(1); }}
                className="w-full h-full bg-transparent pl-9 pr-9 text-sm text-gray-300 outline-none placeholder:text-gray-500"
              />
              {searchQuery && (
                <button onClick={() => setSearchQuery('')} className="absolute right-2 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center rounded-full bg-[#444447] text-gray-400 hover:bg-[#EE2B2A]/20 hover:text-[#EE2B2A]">
                  <FaTimes className="text-[9px]" />
                </button>
              )}
            </div> */}
            {/* Add User button */}
            <button
              onClick={() => setShowAddModal(true)}
              className="flex items-center justify-center gap-2 h-10 px-6 py-2 bg-[#EE2A2B] text-white rounded text-sm font-medium whitespace-nowrap transition-all duration-200 hover:bg-[#d41f20]"
            >
              <FaPlus className="text-xs" /> Add User
            </button>
          </div>
        </div>

        {/* Table */}
        {loading ? (
          <div className="flex items-center justify-center py-20 text-gray-500">
            <span className="w-6 h-6 border-2 border-gray-600 border-t-[#EE2B2A] rounded-full animate-spin mr-3" />
            Loading users…
          </div>
        ) : (
          <div className="w-full overflow-x-auto rounded-xl border border-[#444447]">
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-[#444447]">
                  {['#', 'Name', 'Email', 'Mobile Number', 'Industry', 'Role', 'Status', 'Actions'].map(h => (
                    <th key={h} className={`p-4 text-gray-300 font-semibold text-sm uppercase tracking-wider border-b border-gray-800 ${['#', 'Status', 'Actions'].includes(h) ? 'text-center' : 'text-left'}`}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredUsers.length > 0 ? filteredUsers.map((user, index) => {
                  const isEditing = editingUserId === user._id;
                  return (
                    <React.Fragment key={user._id}>
                      <tr className="bg-[#1E1F22] hover:bg-[#2A2A2B] border-b border-[#444447] transition-all duration-200">
                        <td className="p-3 text-gray-400 text-center text-sm">{(currentPage - 1) * limit + index + 1}</td>
                        <td className="p-3">
                          {isEditing ? (
                            <input type="text" value={editData.name} onChange={e => setEditData(p => ({ ...p, name: e.target.value }))} className="w-full px-2 py-1.5 rounded-lg bg-[#2d2e32] text-gray-200 border border-[#2e2f33] text-sm outline-none focus:border-[#EE2B2A]" />
                          ) : (
                            <div className="flex items-center gap-2">
                              <div className="w-8 h-8 rounded-full bg-[#2d2e32] border border-[#444447] flex items-center justify-center text-gray-400 shrink-0">
                                <FaUserAlt className="text-xs" />
                              </div>
                              <span className="text-gray-200 font-medium text-sm">{user.name}</span>
                            </div>
                          )}
                        </td>
                        <td className="p-3 text-gray-400 text-sm">{user.email}</td>
                        <td className="p-3">
                          {isEditing ? (
                            <input type="text" value={editData.phoneNumber} onChange={e => setEditData(p => ({ ...p, phoneNumber: e.target.value }))} className="w-full px-2 py-1.5 rounded-lg bg-[#2d2e32] text-gray-200 border border-[#2e2f33] text-sm outline-none focus:border-[#EE2B2A]" />
                          ) : <span className="text-gray-400 text-sm">{user.phoneNumber || '—'}</span>}
                        </td>
                        <td className="p-3">
                          {isEditing ? (
                            <select value={editData.industry} onChange={e => setEditData(p => ({ ...p, industry: e.target.value }))} className="w-full px-2 py-1.5 rounded-lg bg-[#2d2e32] text-gray-200 border border-[#2e2f33] text-sm outline-none focus:border-[#EE2B2A]">
                              <option value="">Select…</option>
                              {INDUSTRIES.map(i => <option key={i} value={i}>{i}</option>)}
                            </select>
                          ) : (
                            <span className="px-2.5 py-1 bg-[#2d2e32] text-gray-300 rounded-full text-xs border border-[#444447]">
                              {user.industry || '—'}
                            </span>
                          )}
                        </td>
                        <td className="p-3">
                          <span className="px-2.5 py-1 bg-[#444445] text-white rounded-full text-xs border border-blue-500/30">{user.role}</span>
                        </td>
                        <td className="p-3 text-center">
                          <select
                            value={user.status}
                            onChange={e => handleStatusChange(user._id, e.target.value)}
                            className={`px-2 py-1 rounded-full text-xs font-semibold border outline-none cursor-pointer transition-all duration-200
                              ${user.status === 'active' ? 'bg-green-500/15 text-green-400 border-green-500/30' : 'bg-red-500/15 text-red-400 border-red-500/30'}`}
                          >
                            <option value="active">Active</option>
                            <option value="inactive">Inactive</option>
                          </select>
                        </td>
                        <td className="p-3 text-center">
                          {isEditing ? (
                            <div className="flex items-center justify-center gap-2">
                              <button onClick={() => saveEdit(user._id)} disabled={editSaving} className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-green-500/15 hover:bg-green-500/25 text-green-400 border border-green-500/30 transition-all duration-200 disabled:opacity-50" title="Save">
                                {editSaving ? <span className="w-3 h-3 border border-green-400/25 border-t-green-400 rounded-full animate-spin" /> : <FaCheck className="text-xs" />}
                              </button>
                              <button onClick={cancelEditing} className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-[#2d2e32] hover:bg-[#3a3b3f] text-gray-400 hover:text-gray-200 border border-[#444447] transition-all duration-200" title="Cancel">
                                <FaTimes className="text-xs" />
                              </button>
                            </div>
                          ) : (
                            <button onClick={() => startEditing(user)} className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-transparent hover:bg-[#2d2e32] text-gray-400 hover:text-gray-200 border border-transparent hover:border-[#444447] transition-all duration-200" title="Edit">
                              <FaEdit className="text-sm" />
                            </button>
                          )}
                        </td>
                      </tr>
                      {/* Password edit row */}
                      {isEditing && (
                        <tr className="bg-[#25262b] border-b border-[#444447]">
                          <td colSpan="8" className="px-6 py-4">
                            <label className="flex items-center gap-2 cursor-pointer mb-3">
                              <input type="checkbox" checked={showPasswordEdit} onChange={e => setShowPasswordEdit(e.target.checked)} className="w-4 h-4 accent-[#EE2B2A] cursor-pointer" />
                              <span className="text-sm text-gray-400 select-none">Change Password</span>
                            </label>
                            {showPasswordEdit && (
                              <div className="grid grid-cols-2 gap-4 mt-1">
                                <div>
                                  <label className={labelCls}>New Password</label>
                                  <input type="password" value={editData.password} onChange={e => setEditData(p => ({ ...p, password: e.target.value }))} placeholder="Enter new password" className={inputCls} />
                                </div>
                                <div>
                                  <label className={labelCls}>Confirm Password</label>
                                  <input type="password" value={editData.password_confirmation} onChange={e => setEditData(p => ({ ...p, password_confirmation: e.target.value }))} placeholder="Re-enter password" className={inputCls} />
                                </div>
                              </div>
                            )}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                }) : (
                  <tr>
                    <td colSpan="8" className="p-12 text-center text-gray-500">
                      <div className="flex flex-col items-center gap-3">
                        <FaSearch className="text-4xl opacity-50" />
                        <p className="text-lg">{searchQuery ? `No users match "${searchQuery}"` : 'No users found'}</p>
                        <p className="text-sm">Try adjusting your search</p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {!loading && totalPages > 1 && (
          <div className="flex items-center justify-between mt-6 pt-4 border-t border-[#444447]">
            <p className="text-sm text-gray-500">
              Page {currentPage} of {totalPages}
            </p>
            <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={p => setCurrentPage(p)} />
          </div>
        )}
      </div>

      {/* Add User Modal */}
      <AddUserModal
        show={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSuccess={() => fetchUsers(currentPage)}
      />
    </div>
  );
};

export default Users;
